import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  ClipboardCheck, 
  Bell, 
  User, 
  LogOut, 
  Settings,
  Users,
  Menu,
  X,
  Hammer,
  Wifi,
  WifiOff,
  Inbox,
  Download
} from 'lucide-react';
import axios from 'axios';
import { OnlineStatus } from './OfflineIndicator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Header() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200/80 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3 group" data-testid="header-logo">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
              <ClipboardCheck className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold text-slate-800 hidden sm:block tracking-tight">
              QA BUILDER PRO
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            <Link 
              to="/dashboard" 
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
              data-testid="nav-dashboard"
            >
              Dashboard
            </Link>
            <Link 
              to="/templates" 
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
              data-testid="nav-templates"
            >
              New Form
            </Link>
            {isAdmin() && (
              <>
                <Link 
                  to="/requests" 
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                  data-testid="nav-requests"
                >
                  Requests
                </Link>
                <Link 
                  to="/users" 
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                  data-testid="nav-users"
                >
                  Users
                </Link>
                <Link 
                  to="/trades" 
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                  data-testid="nav-trades"
                >
                  Trades
                </Link>
              </>
            )}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Download App Button */}
            <Button 
              variant="outline" 
              size="sm" 
              className="hidden sm:flex items-center gap-2 text-primary border-primary/30 hover:bg-primary/5"
              onClick={() => {
                if (window.deferredPrompt) {
                  window.deferredPrompt.prompt();
                  window.deferredPrompt.userChoice.then((choiceResult) => {
                    window.deferredPrompt = null;
                  });
                } else {
                  alert('To install: Click the browser menu and select "Install App" or "Add to Home Screen"');
                }
              }}
              data-testid="download-app-btn"
            >
              <Download className="h-4 w-4" />
              Install App
            </Button>
            
            {/* Online Status */}
            <div className="hidden sm:block">
              <OnlineStatus />
            </div>
            
            {/* Notifications */}
            <Link to="/notifications" data-testid="notifications-btn">
              <Button variant="ghost" size="icon" className="relative hover:bg-slate-100">
                <Bell className="h-5 w-5 text-slate-600" />
                {unreadCount > 0 && (
                  <Badge 
                    className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-orange-500 text-white text-xs font-bold shadow-md"
                    data-testid="notification-badge"
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </Button>
            </Link>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-slate-100" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 shadow-lg border-slate-200">
                <div className="px-3 py-2 bg-slate-50 rounded-t-lg">
                  <p className="text-sm font-semibold text-slate-800">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                  <Badge className="mt-1.5 text-xs bg-primary/10 text-primary hover:bg-primary/10">
                    {user?.role === 'admin' ? 'Admin' : user?.role === 'manager' ? 'Manager' : 'Operative'}
                  </Badge>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer" data-testid="profile-menu-item">
                  <Settings className="mr-2 h-4 w-4 text-slate-500" />
                  Profile Settings
                </DropdownMenuItem>
                {isAdmin() && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/requests')} className="cursor-pointer" data-testid="requests-menu-item">
                      <Inbox className="mr-2 h-4 w-4 text-slate-500" />
                      Pending Requests
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/users')} className="cursor-pointer" data-testid="users-menu-item">
                      <Users className="mr-2 h-4 w-4 text-slate-500" />
                      Manage Users
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/trades')} className="cursor-pointer" data-testid="trades-menu-item">
                      <Hammer className="mr-2 h-4 w-4 text-slate-500" />
                      Manage Trades
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer focus:text-red-600 focus:bg-red-50" data-testid="logout-menu-item">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile menu button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-1">
              <button
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-primary bg-primary/5 hover:bg-primary/10 rounded-lg transition-colors"
                onClick={() => {
                  setMobileMenuOpen(false);
                  if (window.deferredPrompt) {
                    window.deferredPrompt.prompt();
                    window.deferredPrompt.userChoice.then((choiceResult) => {
                      window.deferredPrompt = null;
                    });
                  } else {
                    alert('To install: Click the browser menu and select "Install App" or "Add to Home Screen"');
                  }
                }}
              >
                <Download className="h-4 w-4" />
                Install App
              </button>
              <Link 
                to="/dashboard" 
                className="px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Dashboard
              </Link>
              <Link 
                to="/templates" 
                className="px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                New Form
              </Link>
              {isAdmin() && (
                <>
                  <Link 
                    to="/requests" 
                    className="px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Requests
                  </Link>
                  <Link 
                    to="/users" 
                    className="px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Users
                  </Link>
                  <Link 
                    to="/trades" 
                    className="px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Trades
                  </Link>
                </>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
